import * as THREE from 'three';

// ─────────────────────────────────────────────────────────────────────────────
// BeRAM delivery drone + parcel for the 3D map, built to the reference render
// "BeRAM Hexacopter Delivery Drone.png" (repo root).
//
// Drone — model units, Y up, nose toward +Z, origin at the cargo latch under the
// belly (the carried parcel and the touch-drop logic in Map3DRenderer measure
// from it). Propeller groups are named 'propeller' so findRotorSpinners spins
// them. ~4 m per unit on screen (DRONE_MODEL_SCALE_METERS × scaleMultiplier).
//
// Parcel — unit frame, Z up (it is drawn in mercator space): 1.0 wide (x),
// 0.8 deep (y), 0.7 tall: z from −PARCEL_HALF_HEIGHT to +PARCEL_HALF_HEIGHT. Its front
// (logo) faces −Y, which a −90° X rotation turns to the drone's nose.
// ─────────────────────────────────────────────────────────────────────────────

export const PARCEL_HALF_HEIGHT = 0.35;

const C = {
  carbon: 0x24272c,
  hull: 0x15181d,
  shell: 0x9aa3ad,
  black: 0x0d0f12,
  orange: 0xff7a1a,
  cyan: 0x00e5ff,
  kraft: '#c46f2a',
  kraftDark: '#9c5420',
};

const std = (color, roughness, metalness, extra = {}) => new THREE.MeshStandardMaterial({ color, roughness, metalness, ...extra });
const glow = (color, intensity = 2.2) => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: intensity, roughness: 0.3 });

function canvasTexture(w, h, draw) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  const redraw = () => { draw(canvas.getContext('2d'), w, h); tex.needsUpdate = true; };
  redraw();
  return { tex, redraw };
}

// The brand logo (public/BERAMLOGO.png: white atom on a blue disc, as in the app
// header), loaded once. Callbacks redraw the textures that use it on load.
let logoImg = null;
const logoWaiters = [];
function withLogo(onReady) {
  if (logoImg) return logoImg;
  logoWaiters.push(onReady);
  if (logoWaiters.length === 1 && typeof Image !== 'undefined') {
    const img = new Image();
    img.onload = () => { logoImg = img; logoWaiters.splice(0).forEach((fn) => fn()); };
    img.src = `${process.env.PUBLIC_URL || ''}/BERAMLOGO.png`;
  }
  return null;
}

function drawWordmark(ctx, cx, cy, size, { lines = true, text = 'BERAM' } = {}) {
  ctx.save();
  ctx.font = `italic 900 ${size}px "Arial Black", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = size * 0.08;
  ctx.fillText(text, cx, cy);
  ctx.shadowBlur = 0;
  if (lines) {
    const w = ctx.measureText(text).width / 2;
    ctx.fillStyle = '#00e5ff';
    [-0.22, 0, 0.22].forEach((dy, i) => {
      const len = size * (0.55 - i * 0.12);
      const t = size * 0.09;
      ctx.fillRect(cx - w - size * 0.12 - len, cy + dy * size - t / 2, len, t);
      ctx.fillRect(cx + w + size * 0.12, cy + dy * size - t / 2, len, t);
    });
  }
  ctx.restore();
}

function drawAtomSeal(ctx, cx, cy, r) {
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#0b1218'; ctx.fill();
  ctx.lineWidth = r * 0.09; ctx.strokeStyle = '#00e5ff';
  ctx.shadowColor = '#00e5ff'; ctx.shadowBlur = r * 0.25; ctx.stroke(); ctx.shadowBlur = 0;
  if (logoImg) {
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.9, 0, Math.PI * 2); ctx.clip();
    ctx.drawImage(logoImg, cx - r * 0.9, cy - r * 0.9, r * 1.8, r * 1.8);
  }
  ctx.restore();
}

function drawKraft(ctx, w, h) {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, '#c06a2a'); g.addColorStop(1, '#93501d');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  // cardboard grain + worn edges
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = `rgba(${Math.random() < 0.5 ? '60,30,10' : '255,210,160'},${Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * w, Math.random() * h, 1 + Math.random() * 3, 1);
  }
  const v = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.75);
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(40,15,0,0.35)');
  ctx.fillStyle = v; ctx.fillRect(0, 0, w, h);
}

function drawHazard(ctx, w, y, hBand) {
  ctx.save();
  ctx.fillStyle = '#ff8a1c'; ctx.fillRect(0, y, w, hBand);
  ctx.beginPath(); ctx.rect(0, y, w, hBand); ctx.clip();
  ctx.fillStyle = '#121212';
  const step = hBand * 1.1;
  for (let x = -hBand; x < w + hBand; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, y + hBand); ctx.lineTo(x + step / 2, y + hBand); ctx.lineTo(x + step / 2 + hBand, y); ctx.lineTo(x + hBand, y);
    ctx.fill();
  }
  ctx.restore();
}

let parcelTextures = null;
function getParcelTextures() {
  if (parcelTextures) return parcelTextures;
  const front = canvasTexture(512, 322, (ctx, w, h) => {
    drawKraft(ctx, w, h);
    drawHazard(ctx, w, h - 40, 40);
    drawAtomSeal(ctx, w / 2, 122, 74);
    drawWordmark(ctx, w / 2, 236, 46);  // BERAM
  });
  const side = canvasTexture(410, 322, (ctx, w, h) => {
    drawKraft(ctx, w, h);
    drawHazard(ctx, w, h - 40, 40);
    // handling marks: "this way up" and "fragile"
    ctx.strokeStyle = '#1a1006'; ctx.fillStyle = '#1a1006'; ctx.lineWidth = 4;
    const icon = (x, y) => { ctx.strokeRect(x, y, 70, 70); };
    icon(300, 40);
    [318, 352].forEach((ax) => {
      ctx.beginPath(); ctx.moveTo(ax, 98); ctx.lineTo(ax, 58); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - 9, 66); ctx.lineTo(ax, 52); ctx.lineTo(ax + 9, 66); ctx.fill();
    });
    icon(300, 122);
    ctx.beginPath(); ctx.moveTo(320, 136); ctx.lineTo(350, 136); ctx.lineTo(344, 160); ctx.quadraticCurveTo(335, 168, 326, 160); ctx.closePath(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(335, 166); ctx.lineTo(335, 182); ctx.moveTo(325, 182); ctx.lineTo(345, 182); ctx.stroke();
    // waybill barcode
    ctx.fillStyle = '#f5f5f0'; ctx.fillRect(250, 196, 130, 74);
    ctx.fillStyle = '#111';
    for (let i = 0; i < 40; i++) ctx.fillRect(258 + i * 2.85, 203, (i % 3 === 0 || i % 7 === 0) ? 2.2 : 1, 50);
    ctx.font = 'bold 11px monospace'; ctx.textAlign = 'center'; ctx.fillText('BRM 0451 2280 7', 315, 264);
  });
  const top = canvasTexture(512, 410, (ctx, w, h) => {
    drawKraft(ctx, w, h);
    ctx.fillStyle = 'rgba(60,25,5,0.35)'; ctx.fillRect(0, h / 2 - 3, w, 6);  // lid flap seam
    ctx.fillStyle = 'rgba(230,180,120,0.55)'; ctx.fillRect(w / 2 - 26, 0, 52, h);  // packing tape
  });
  parcelTextures = { front: front.tex, side: side.tex, top: top.tex };
  withLogo(() => { front.redraw(); });
  return parcelTextures;
}

// A plane facing `normal` with its top toward `up`, so labels read the right way.
function decal(w, h, map, normal, up, pos) {
  const n = new THREE.Vector3(...normal).normalize();
  const u = new THREE.Vector3(...up).normalize();
  const right = new THREE.Vector3().crossVectors(u, n);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshStandardMaterial({ map, roughness: 0.75, metalness: 0.05 }));
  mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, u, n));
  mesh.position.set(...pos);
  mesh.frustumCulled = false;
  return mesh;
}

// `clamps`: the drone's gripper blocks on the lid — only while the parcel is
// held under the drone. Hanging on the winch or left on the ground it is just
// the box (with the clamps it read as a black frame from above).
export function buildBeramParcel({ clamps = false } = {}) {
  const g = new THREE.Group();
  const t = getParcelTextures();
  // Body z −0.30…+0.33 on a black base −0.35…−0.30.
  const kraft = std(0xa95c22, 0.85, 0.02);
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.8, 0.63), kraft);
  body.position.z = 0.015;
  g.add(body);
  // Branded faces (front/back: seal + wordmark + hazard band; sides: handling marks + barcode)
  g.add(decal(1.0, 0.63, t.front, [0, -1, 0], [0, 0, 1], [0, -0.4012, 0.015]));
  g.add(decal(1.0, 0.63, t.front, [0, 1, 0], [0, 0, 1], [0, 0.4012, 0.015]));
  g.add(decal(0.8, 0.63, t.side, [1, 0, 0], [0, 0, 1], [0.5012, 0, 0.015]));
  g.add(decal(0.8, 0.63, t.side, [-1, 0, 0], [0, 0, 1], [-0.5012, 0, 0.015]));
  g.add(decal(1.0, 0.8, t.top, [0, 0, 1], [0, 1, 0], [0, 0, 0.3312]));
  // Lid lip, black base, the two cargo straps and the gripper clamp blocks
  const lip = new THREE.Mesh(new THREE.BoxGeometry(1.025, 0.825, 0.035), std(0x8a4a18, 0.75, 0.05));
  lip.position.z = 0.31; g.add(lip);
  const base = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.84, 0.05), std(0x141414, 0.6, 0.2));
  base.position.z = -0.325; g.add(base);
  const strapMat = std(0x111214, 0.55, 0.25);
  [-0.3, 0.3].forEach((x) => {
    const strap = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.826, 0.67), strapMat);
    strap.position.set(x, 0, 0.01); g.add(strap);
    if (clamps) [-0.3, 0.3].forEach((y) => {
      const clamp = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.15, 0.06), std(C.black, 0.45, 0.5));
      clamp.position.set(x, y, 0.36); g.add(clamp);
    });
  });
  g.traverse((o) => { o.frustumCulled = false; });
  return g;
}

let carbonTexture = null;
function getCarbonTexture() {
  if (carbonTexture) return carbonTexture;
  carbonTexture = canvasTexture(64, 64, (ctx, w, h) => {
    ctx.fillStyle = '#1b1d21'; ctx.fillRect(0, 0, w, h);
    // 2x2 twill: diagonal steps of lighter tow
    for (let y = 0; y < h; y += 8) {
      for (let x = 0; x < w; x += 8) {
        if (((x / 8) + (y / 8)) % 4 < 2) { ctx.fillStyle = '#34373d'; ctx.fillRect(x, y, 8, 8); }
      }
    }
  }).tex;
  carbonTexture.wrapS = THREE.RepeatWrapping;
  carbonTexture.wrapT = THREE.RepeatWrapping;
  carbonTexture.repeat.set(2, 14);
  return carbonTexture;
}

let droneTextures = null;
function getDroneTextures() {
  if (droneTextures) return droneTextures;
  // Front brand panel, like the app header: logo disc + BERAM, large and crisp.
  const wordmark = canvasTexture(1024, 528, (ctx, w, h) => {
    ctx.fillStyle = '#0b0d10'; ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = '#00e5ff'; ctx.lineWidth = 10; ctx.strokeRect(5, 5, w - 10, h - 10);
    drawAtomSeal(ctx, 200, h / 2, 150);  // logo disc: x 50..350
    // Lettering fills the rest (x 390..990) — sized to fit, never clipped.
    let size = 200;
    const font = (px) => `bold ${px}px "Segoe UI", Arial, sans-serif`;
    ctx.font = font(size);
    while (ctx.measureText('BERAM').width > 570 && size > 60) { size -= 4; ctx.font = font(size); }
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,229,255,0.55)'; ctx.shadowBlur = 18;
    ctx.fillText('BERAM', 690, h / 2 + 6);
  });
  const atom = canvasTexture(512, 512, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    if (logoImg) ctx.drawImage(logoImg, 0, 0, w, h);
  });
  droneTextures = { wordmark: wordmark.tex, atom: atom.tex };
  withLogo(() => { atom.redraw(); wordmark.redraw(); });
  return droneTextures;
}

// Cylinder between two points (legs, arms).
function tube(from, to, r, mat, segments = 14) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const dir = new THREE.Vector3().subVectors(b, a);
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, dir.length(), segments), mat);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  return m;
}

// Regular polygon prism along Y with rounded (bevelled) edges, centred on y.
// A flat face looks toward +Z.
function bevelledPrism(radius, height, sides, bevel, mat, y) {
  const shape = new THREE.Shape();
  for (let i = 0; i <= sides; i++) {
    const a = (i / sides) * Math.PI * 2 + Math.PI / sides;
    const x = Math.sin(a) * (radius - bevel);
    const z = Math.cos(a) * (radius - bevel);
    if (i === 0) shape.moveTo(x, z); else shape.lineTo(x, z);
  }
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(0.001, height - 2 * bevel), bevelEnabled: true,
    bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3, curveSegments: 1,
  });
  geo.rotateX(Math.PI / 2);  // extrusion (+Z) → down (−Y); shape plane → XZ
  geo.translate(0, height / 2 - bevel, 0);
  const m = new THREE.Mesh(geo, mat);
  m.position.y = y;
  return m;
}

// One propeller blade along −Z: tapered planform, thin, pitched; the last 15 %
// is the orange safety tip.
function blade(length, black, orange) {
  // Slim planform (~10 % of the length at its widest), as on the reference.
  const width = (t) => length * (t < 0.3 ? 0.075 + 0.08 * t : 0.099 - 0.055 * ((t - 0.3) / 0.7));
  const outline = (from, to) => {
    const sh = new THREE.Shape();
    for (let i = 0; i <= 8; i++) {
      const t = from + ((to - from) * i) / 8;
      if (i === 0) sh.moveTo(width(t) / 2, t * length); else sh.lineTo(width(t) / 2, t * length);
    }
    for (let i = 8; i >= 0; i--) {
      const t = from + ((to - from) * i) / 8;
      sh.lineTo(-width(t) * 0.35, t * length);
    }
    const geo = new THREE.ExtrudeGeometry(sh, { depth: 0.0025, bevelEnabled: false });
    geo.rotateX(-Math.PI / 2);  // length (shape Y) → −Z, thickness → +Y
    return geo;
  };
  const g = new THREE.Group();
  g.add(new THREE.Mesh(outline(0.06, 0.88), black));
  g.add(new THREE.Mesh(outline(0.88, 1.0), orange));
  g.rotation.z = 0.16;  // pitch
  return g;
}

export function buildBeramDeliveryDrone(arms = 6) {
  const g = new THREE.Group();
  const tex = getDroneTextures();
  // Blacks are low-metalness on purpose. On the map the model is drawn through
  // Mapbox's projection, where three.js has no real camera position, so a metal
  // reflects one bright patch of the environment from every angle — the black
  // hull, motors and legs came out chrome. Only the shell and caps are metal.
  const hull = std(0x111317, 0.5, 0.2);
  const silver = std(0xd2d8de, 0.22, 0.9, { flatShading: true });
  const darkPanel = std(0x1a1d22, 0.45, 0.25);
  const glossBlack = std(0x0b0c0f, 0.32, 0.25);
  const chrome = std(0xf0f3f6, 0.12, 1.0);
  const carbon = std(0x5d6168, 0.42, 0.2, { map: getCarbonTexture() });
  const orange = std(0xff7417, 0.42, 0.15, { emissive: 0xff7417, emissiveIntensity: 0.18 });
  const cyan = glow(0x00e5ff, 2.6);

  const OCT = Math.PI / 8;
  const R = 0.11;                       // hull radius (to a corner)
  const apothem = R * Math.cos(OCT);    // to a flat face
  // The reference fuselage is wider than deep, with a broad front face for the
  // wordmark: the hull parts are built round and stretched sideways.
  const FUSE_X = 1.3;
  const fuse = new THREE.Group();
  fuse.scale.x = FUSE_X;
  g.add(fuse);

  // ── Fuselage ──
  fuse.add(bevelledPrism(R, 0.052, 8, 0.008, hull, 0.038));            // black hull, rounded edges
  const belly = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.96, 0.07, 0.016, 8), hull);
  belly.rotation.y = OCT; belly.position.y = 0.006; fuse.add(belly);
  const shell = new THREE.Mesh(new THREE.CylinderGeometry(0.07, R * 1.01, 0.036, 8, 1), silver);
  shell.rotation.y = OCT; shell.position.y = 0.082; fuse.add(shell);   // faceted silver top shell
  fuse.add(bevelledPrism(0.07, 0.008, 8, 0.002, darkPanel, 0.103));    // top deck

  // Front: dark panel with the BeRAM wordmark on the shell's front facet
  const rTop = 0.07 * Math.cos(OCT);
  const rBot = R * 1.01 * Math.cos(OCT);
  const n = new THREE.Vector3(0, rBot - rTop, 0.036).normalize();
  const up = new THREE.Vector3(0, 0.036, -(rBot - rTop)).normalize();
  const facetCenter = new THREE.Vector3(0, 0.082, (rTop + rBot) / 2).addScaledVector(n, 0.0012);
  // Added unstretched (not to `fuse`) so the lettering keeps its proportions.
  const brand = decal(FUSE_X * 2 * ((rTop + rBot) / 2) * Math.tan(OCT) * 0.98, 0.046, tex.wordmark, n.toArray(), up.toArray(), facetCenter.toArray());
  // Lit from within so the logo and lettering read on a dark map.
  Object.assign(brand.material, { emissive: new THREE.Color(0xffffff), emissiveMap: tex.wordmark, emissiveIntensity: 0.55 });
  g.add(brand);

  // Orange plates on the four diagonal hull faces, cyan speed lines on the shell
  [Math.PI / 4, -Math.PI / 4, (3 * Math.PI) / 4, (-3 * Math.PI) / 4].forEach((a) => {
    const dir = new THREE.Vector3(Math.sin(a), 0, Math.cos(a));
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.034, 0.003), orange);
    plate.position.copy(dir.clone().multiplyScalar(apothem + 0.0015)).setY(0.04);
    plate.rotation.y = a; fuse.add(plate);
    if (Math.cos(a) > 0) {  // front shoulders only, as in the render
      [0, 1].forEach((k) => {
        const line = new THREE.Mesh(new THREE.BoxGeometry(0.022 - k * 0.006, 0.0025, 0.0025), cyan);
        line.position.copy(dir.clone().multiplyScalar(0.088 - k * 0.006)).setY(0.075 + k * 0.006);
        line.rotation.y = a; fuse.add(line);
      });
    }
  });

  // LED strip under the wordmark, camera gimbal under the nose
  const ledFrame = new THREE.Mesh(new THREE.BoxGeometry(0.066, 0.012, 0.003), darkPanel);
  ledFrame.position.set(0, 0.04, apothem + 0.0012); fuse.add(ledFrame);
  for (let i = 0; i < 7; i++) {
    const led = new THREE.Mesh(new THREE.BoxGeometry(0.0055, 0.0045, 0.002), cyan);
    led.position.set((i - 3) * 0.0085, 0.04, apothem + 0.0028); fuse.add(led);
  }
  const yoke = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.012, 0.016), glossBlack);
  yoke.position.set(0, -0.004, 0.068); g.add(yoke);
  const cam = new THREE.Mesh(new THREE.SphereGeometry(0.017, 24, 16), glossBlack);
  cam.position.set(0, -0.019, 0.07); g.add(cam);
  const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.0085, 0.0085, 0.006, 20), std(0x05070a, 0.04, 1.0));
  lens.rotation.x = Math.PI / 2; lens.position.set(0, -0.019, 0.087); g.add(lens);
  const lensRing = new THREE.Mesh(new THREE.TorusGeometry(0.0095, 0.0018, 8, 24), chrome);
  lensRing.position.set(0, -0.019, 0.089); g.add(lensRing);

  // Top: atom medallion in a thin cyan ring, GPS puck on a mast at the back
  const medallion = new THREE.Mesh(new THREE.CircleGeometry(0.05, 40), std(0x080b10, 0.25, 0.6));
  medallion.rotation.x = -Math.PI / 2; medallion.position.y = 0.1075; g.add(medallion);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.0028, 10, 48), cyan);
  ring.rotation.x = Math.PI / 2; ring.position.y = 0.108; g.add(ring);
  const atom = new THREE.Mesh(new THREE.CircleGeometry(0.046, 48), new THREE.MeshStandardMaterial({
    map: tex.atom, emissiveMap: tex.atom, emissive: 0xffffff, emissiveIntensity: 0.45, transparent: true, roughness: 0.35, metalness: 0,
  }));
  atom.rotation.set(-Math.PI / 2, 0, Math.PI); atom.position.y = 0.1085; g.add(atom);
  g.add(tube([0, 0.106, -0.052], [0, 0.17, -0.052], 0.0045, glossBlack));
  const puck = new THREE.Mesh(new THREE.CylinderGeometry(0.027, 0.03, 0.013, 32), std(0xeef1f4, 0.35, 0.15));
  puck.position.set(0, 0.176, -0.052); g.add(puck);
  const puckBase = new THREE.Mesh(new THREE.CylinderGeometry(0.031, 0.031, 0.004, 32), glossBlack);
  puckBase.position.set(0, 0.168, -0.052); g.add(puckBase);
  const puckRing = new THREE.Mesh(new THREE.TorusGeometry(0.0305, 0.0022, 8, 40), cyan);
  puckRing.rotation.x = Math.PI / 2; puckRing.position.set(0, 0.1655, -0.052); g.add(puckRing);

  // Belly plate at the cargo latch (origin)
  const plate = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.008, 0.14), glossBlack);
  plate.position.y = -0.002; g.add(plate);

  // ── Arms, motors, propellers ──
  const armLen = 0.3;
  const armY = 0.042;
  const angles = arms === 6
    ? Array.from({ length: 6 }, (_, i) => Math.PI / 6 + (i * Math.PI) / 3)  // left and right arms straight out
    : [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
  const bladeBlack = std(0x0e0f12, 0.4, 0.1, { side: THREE.DoubleSide });
  const bladeOrange = std(0xff7417, 0.4, 0.1, { side: THREE.DoubleSide, emissive: 0xff7417, emissiveIntensity: 0.2 });
  angles.forEach((a) => {
    const dx = Math.sin(a);
    const dz = Math.cos(a);
    const at = (r, y) => [dx * r, y, dz * r];
    g.add(tube(at(0.085, armY), at(armLen - 0.012, armY), 0.0105, carbon, 16));
    g.add(tube(at(0.085, armY), at(0.108, armY), 0.0145, glossBlack, 16));          // arm root collar

    const [mx, , mz] = at(armLen, 0);
    const mount = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.026, 20), glossBlack);
    mount.position.set(mx, armY, mz); g.add(mount);
    const can = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.026, 0.028, 32), glossBlack);
    can.position.set(mx, armY + 0.024, mz); g.add(can);
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.0263, 0.0263, 0.005, 32, 1, true), cyan);
    band.position.set(mx, armY + 0.0125, mz); g.add(band);                              // glow band at the base
    const bell = new THREE.Mesh(new THREE.CylinderGeometry(0.019, 0.025, 0.007, 32), glossBlack);
    bell.position.set(mx, armY + 0.0415, mz); g.add(bell);
    const spinner = new THREE.Mesh(new THREE.SphereGeometry(0.0065, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2), chrome);
    spinner.position.set(mx, armY + 0.045, mz); g.add(spinner);

    const prop = new THREE.Group();
    prop.name = 'propeller';
    prop.position.set(mx, armY + 0.047, mz);
    prop.rotation.y = a + Math.PI / 2;  // at rest across the arm, as in the render
    [0, Math.PI].forEach((r) => {
      const holder = new THREE.Group();
      holder.rotation.y = r;
      holder.add(blade(0.14, bladeBlack, bladeOrange));
      prop.add(holder);
    });
    g.add(prop);
  });

  // ── Landing gear: four splayed legs to two skids with rounded, glowing ends ──
  // Wide enough at the top that the carried parcel (±0.133 x with its lip,
  // −0.015…−0.197 y) sits between the legs without touching them.
  const legMat = std(0x0f1114, 0.4, 0.2);
  const skidY = -0.215;
  const skidX = 0.19;
  [-1, 1].forEach((sx) => {
    [-1, 1].forEach((sz) => g.add(tube([sx * 0.135, 0.012, sz * 0.055], [sx * skidX, skidY, sz * 0.09], 0.0058, legMat)));
    g.add(tube([sx * skidX, skidY, -0.16], [sx * skidX, skidY, 0.16], 0.0068, legMat));
    [-1, 1].forEach((ez) => {
      const cap = new THREE.Mesh(new THREE.CapsuleGeometry(0.0105, 0.022, 6, 16), glossBlack);
      cap.rotation.x = Math.PI / 2; cap.position.set(sx * skidX, skidY, ez * 0.168); g.add(cap);
      const capGlow = new THREE.Mesh(new THREE.TorusGeometry(0.0108, 0.0026, 8, 24), cyan);
      capGlow.position.set(sx * skidX, skidY, ez * 0.146); g.add(capGlow);
    });
  });

  return g;
}

